/**
 * 顾问陪跑 · 交叉用户（草案 A-5，PR⑧，仅超管）。
 *
 * 顾问成交时付款人已经归属某个城市合伙人 → 这单的合伙人佣金挂起（不进合伙人佣金表、合伙人看不到）。
 * 在这里逐单判定：
 *   · 发给合伙人：生成佣金条目。书已确认制作 → 直接可结算；否则等代下印刷单时转可结算。之后退款照常作废
 *   · 不发：必须写理由（合伙人来问时说得清）
 * 判定不可撤销。
 *
 * 下方「只打了标记」：合伙人归属发生在顾问成交之后，那一单与合伙人无关，不涉及佣金，仅供知悉。
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Card, Empty, Input, Modal, Radio, Space, Table, Tag, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listAdvisorOverlaps,
  resolveAdvisorCommissionHold,
  type AdvisorOverlapFlagOnlyDto,
  type AdvisorOverlapOrderDto,
  type OverlapStatusFilter,
} from '../../../api/advisor';

const { Text } = Typography;

const yuan = (fen: number | null) => (fen == null ? '-' : `¥${(fen / 100).toFixed(fen % 100 === 0 ? 0 : 2)}`);
const fmt = (ms: number | null) => (ms == null ? '-' : new Date(ms).toLocaleString('zh-CN', { hour12: false }));

const HOLD_META: Record<string, { color: string; label: string }> = {
  on_hold: { color: 'orange', label: '待判定' },
  released: { color: 'green', label: '已发给合伙人' },
  waived: { color: 'default', label: '不发' },
  void: { color: 'red', label: '已退款作废' },
};

const ENTRY_LABEL: Record<string, string> = {
  pending: '待确认制作',
  payable: '可结算',
  settled: '已结算',
  void: '已作废',
};

const errMsg = (e: any, fallback: string) => e?.response?.data?.message ?? fallback;

export default function AdvisorOverlapsPage() {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<OverlapStatusFilter>('on_hold');
  const query = useQuery({ queryKey: ['advisor-overlaps', status], queryFn: () => listAdvisorOverlaps(status) });

  const [target, setTarget] = useState<{ order: AdvisorOverlapOrderDto; decision: 'release' | 'waive' } | null>(null);
  const [note, setNote] = useState('');

  const resolveMutation = useMutation({
    mutationFn: () => resolveAdvisorCommissionHold(target!.order.advisorOrderId, { decision: target!.decision, note: note.trim() || undefined }),
    onSuccess: (data) => {
      message.success(
        data.commissionHoldStatus === 'released'
          ? `已发给合伙人（佣金${data.entryStatus === 'payable' ? '已可结算' : '等确认制作后可结算'}）`
          : '已判定不发',
      );
      setTarget(null);
      setNote('');
      queryClient.invalidateQueries({ queryKey: ['advisor-overlaps'] });
    },
    onError: (e) => {
      message.error(errMsg(e, '判定失败'));
    },
  });

  const columns: ColumnsType<AdvisorOverlapOrderDto> = [
    {
      title: '客户',
      render: (_, o) => (
        <Space direction="vertical" size={0}>
          <Link to={`/advisor/customers/${o.customerId}`}>{o.customerName}</Link>
          <Text type="secondary">销售 {o.advisorNickname ?? '-'}</Text>
        </Space>
      ),
    },
    {
      title: '合伙人',
      render: (_, o) => (
        <Space direction="vertical" size={0}>
          <Link to="/partners">{o.partner.name ?? o.partner.partnerId ?? '-'}</Link>
          <Text type="secondary">
            {o.partner.city ?? ''}
            {o.partner.status && o.partner.status !== 'active' ? ` · ${o.partner.status === 'stopped' ? '已停止合作' : o.partner.status}` : ''}
          </Text>
        </Space>
      ),
    },
    {
      title: '顾问订单',
      render: (_, o) => (
        <Space direction="vertical" size={0}>
          <Text>{yuan(o.amountFen)}</Text>
          <Text type="secondary">付款 {fmt(o.paidAt)}</Text>
        </Space>
      ),
    },
    {
      title: '挂起佣金',
      render: (_, o) => (
        <Space direction="vertical" size={0}>
          <Text>{yuan(o.holdFen)}</Text>
          <Text type="secondary">{o.printCommitted ? '书已确认制作' : '书未确认制作'}</Text>
        </Space>
      ),
    },
    {
      title: '判定',
      render: (_, o) => (
        <Space direction="vertical" size={0}>
          <Tag color={HOLD_META[o.holdStatus]?.color}>{HOLD_META[o.holdStatus]?.label ?? o.holdStatus}</Tag>
          {o.resolvedAt && (
            <Text type="secondary">
              {o.resolvedBy ?? '-'} · {fmt(o.resolvedAt)}
            </Text>
          )}
          {o.note && <Text type="secondary">理由：{o.note}</Text>}
          {o.entryStatus && <Text type="secondary">佣金条目：{ENTRY_LABEL[o.entryStatus] ?? o.entryStatus}</Text>}
        </Space>
      ),
    },
    {
      title: '',
      render: (_, o) =>
        o.holdStatus === 'on_hold' ? (
          <Space>
            <Button size="small" type="primary" onClick={() => { setNote(''); setTarget({ order: o, decision: 'release' }); }}>
              发给合伙人
            </Button>
            <Button size="small" onClick={() => { setNote(''); setTarget({ order: o, decision: 'waive' }); }}>
              不发
            </Button>
          </Space>
        ) : null,
    },
  ];

  const flagColumns: ColumnsType<AdvisorOverlapFlagOnlyDto> = [
    { title: '客户', render: (_, c) => <Link to={`/advisor/customers/${c.customerId}`}>{c.customerName}</Link> },
    { title: '销售', render: (_, c) => c.advisorNickname ?? '-' },
    { title: '合伙人', render: (_, c) => c.partnerName ?? c.partnerId },
    { title: '标记时间', render: (_, c) => fmt(c.detectedAt) },
  ];

  const waiveNeedsNote = target?.decision === 'waive' && !note.trim();

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card
        title="交叉用户 · 合伙人佣金判定"
        extra={
          <Radio.Group value={status} onChange={(e) => setStatus(e.target.value)} optionType="button" buttonStyle="solid">
            <Radio.Button value="on_hold">待判定</Radio.Button>
            <Radio.Button value="resolved">已判定</Radio.Button>
            <Radio.Button value="all">全部</Radio.Button>
          </Radio.Group>
        }
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Alert
            type="info"
            showIcon
            message="顾问成交时付款人已归属城市合伙人，这单的合伙人佣金先挂起、合伙人看不到。判定后不可撤销。"
          />
          {query.isError ? (
            <Alert type="error" showIcon message={errMsg(query.error, '加载失败')} />
          ) : (
            <Table<AdvisorOverlapOrderDto>
              rowKey="advisorOrderId"
              loading={query.isLoading}
              scroll={{ x: 'max-content' }}
              dataSource={query.data?.orders ?? []}
              columns={columns}
              pagination={false}
              locale={{ emptyText: <Empty description={status === 'on_hold' ? '没有待判定的单' : '没有记录'} /> }}
            />
          )}
        </Space>
      </Card>

      <Card title="只打了标记（不涉及佣金）" size="small">
        <Text type="secondary">合伙人归属发生在顾问成交之后：那一单不是合伙人带来的，不产生佣金，仅供知悉。</Text>
        <Table<AdvisorOverlapFlagOnlyDto>
          rowKey="customerId"
          size="small"
          style={{ marginTop: 8 }}
          dataSource={query.data?.flagOnly ?? []}
          columns={flagColumns}
          pagination={false}
          locale={{ emptyText: '没有' }}
        />
      </Card>

      <Modal
        title={target?.decision === 'release' ? '发给合伙人？' : '不发佣金？'}
        open={!!target}
        onCancel={() => setTarget(null)}
        onOk={() => resolveMutation.mutate()}
        okText={target?.decision === 'release' ? '确认发放' : '确认不发'}
        okButtonProps={{ disabled: waiveNeedsNote, danger: target?.decision === 'waive' }}
        confirmLoading={resolveMutation.isPending}
        destroyOnHidden
      >
        {target && (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text>
              客户「{target.order.customerName}」· 合伙人「{target.order.partner.name ?? target.order.partner.partnerId}」· 挂起{' '}
              {yuan(target.order.holdFen)}
            </Text>
            {target.decision === 'release' ? (
              <Alert
                type="warning"
                showIcon
                message={
                  target.order.printCommitted
                    ? '这本书已确认制作，佣金会直接进入可结算，参加下一期结算。'
                    : '佣金先记为待确认制作，代下印刷单后转为可结算。期间客户退款会自动作废。'
                }
              />
            ) : (
              <Alert type="info" showIcon message="不产生任何佣金。请写清理由，合伙人来问时有据可查。" />
            )}
            <Input.TextArea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={255}
              autoSize={{ minRows: 2, maxRows: 4 }}
              placeholder={target.decision === 'waive' ? '理由（必填）' : '备注（可选）'}
              aria-label="判定理由"
            />
            <Text type="secondary">判定后不可撤销。</Text>
          </Space>
        )}
      </Modal>
    </Space>
  );
}
